import videojs from 'video.js';
import 'videojs-markers';
import Player from 'video.js/dist/types/player';
import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  signal,
  OnDestroy,
  Input,
  EventEmitter,
  Output,
} from '@angular/core';
import { Marker } from '@components/marker/marker.interface';
import { ViewportSize } from '@services/viewport-size.interface';

@Component({
  selector: 'app-video-player',
  templateUrl: './video-player.component.html',
  styles: [],
})
export class VideoPlayerComponent implements OnInit, OnDestroy {
  @ViewChild('videoPlayer', { static: true }) videoElement!: ElementRef;
  @Input() playsinline: boolean; // Default to true
  // See options: https://videojs.com/guides/options
  @Input() options: {
    fluid: boolean;
    autoplay: boolean | string;
    controls: boolean;
    sources: {
      src: string;
      type: string;
    }[];
    controlBar?: { fullscreenToggle: boolean; pictureInPictureToggle: boolean };
    fullscreen?: any;
    fill?: boolean;
    responsive?: boolean;
    aspectRatio?: string;
  };

  @Input() markers: Marker[];

  // Video Events
  @Output() readonly playStarted = new EventEmitter<boolean>();
  @Output() readonly videoEnded = new EventEmitter<void>();

  // Marker Events
  @Output() readonly markerState = new EventEmitter<Marker>();
  @Output() readonly markerClicked = new EventEmitter<Marker>();

  // Resize Events
  @Output() readonly videoResizeEvent = new EventEmitter<ViewportSize>();
  private resizeObserver!: ResizeObserver;

  isEnded = signal(false);
  isPlaying = signal(false);
  private isToggling = false;

  activeMarker = signal<Marker | null>(null); // Add this signal

  private player: Player | null = null;

  private readonly SEEK_TO = 3.0; // seconds

  // Capture view size aspect and window ratio
  aspectRatio: number;
  windowRatio: number;

  constructor() {}

  ngOnInit(): void {
    this.initializePlayer();
  }

  /**
   * Initialize video JS player
   */
  private initializePlayer(): void {
    if (this.videoElement && this.videoElement.nativeElement && this.options) {
      try {
        this.player = videojs(this.videoElement.nativeElement, this.options, () => {
          // console.log('onPlayerReady is ready to play', this);
          this.setupPlayerEvents();
          this.setupResizeObserver();
          this.calculateViewPortSize();
        });
      } catch (error) {
        console.error('Error initializing Video.js:', error);
      }
    } else {
      console.error('Video element not found');
    }
  }

  /**
   * Setup custom Video player event listeners
   */
  private setupPlayerEvents(): void {
    if (this.player) {
      this.player.on('loadedmetadata', this.handleLoadedMetadata.bind(this));
      this.player.on('loadeddata', this.handleLoadedData.bind(this));
      this.player.on('ready', this.setupMarkers.bind(this));
      this.player.on('ended', () => {
        this.isEnded.set(true);
        this.isPlaying.set(false);
        this.videoEnded.emit();
      });
      console.log('setupPlayerEvents is complete');
    }
  }

  /**
   * Handle video loaded metadata event
   * Log video duration
   */
  private handleLoadedMetadata(): void {
    console.log('Video duration:', this.player?.duration());
  }

  /**
   * Handle video loaded data event
   * If video is too short, log an error
   */
  private handleLoadedData(): void {
    if (this.player) {
      const duration = this.player.duration();
      if (duration < this.SEEK_TO) {
        console.error('Video is too short.');
        return;
      }
    }
  }

  /**
   *  Replay video from the beginning
   */
  replayVideo(): void {
    if (this.player) {
      this.player.currentTime(0);
      this.isPlaying.set(false);
      this.player.play();
      this.isPlaying.set(true);
      this.isEnded.set(false);
      this.playStarted.emit(true);
    }
  }

  /**
   * Configure Video Js Markers plugin to set markers on video on intialization
   */
  private setupMarkers(): void {
    if (this.player && (this.player as any).markers) {
      (this.player as any).markers({
        // TODO: Need to get this working in the styles.scss properly
        markerStyle: {
          width: '7px',
          height: '14px',
          'border-radius': '30%',
          'background-color': '#FF451A',
        },
        markerTip: {
          display: true,
          text: (marker: Marker) => `Marker at ${marker.text} - ${marker.time} secs`,
        },
        breakOverlay: {
          display: false,
        },
        onMarkerClick: (marker: Marker) => {
          if (this.activeMarker() === marker) {
            // If same marker is clicked again, resume playback
            this.togglePlay();
            this.activeMarker.set(null);
          } else {
            // If different marker is clicked, pause and update active marker
            if (this.player && !this.player.paused()) {
              // console.log('Resume play from marker');
              this.togglePlay();
            }
            this.activeMarker.set(marker);
          }
          this.markerClicked.emit(marker);
        },
        onMarkerReached: (marker: Marker) => {
          // Emit video marker to calling parent component.
          this.updateVideoOverLay(marker);
        },
        markers: this.markers,
      });
    } else {
      console.error('Video.js player or markers plugin not available');
    }
  }

  /**
   * Update video overlay with marker text
   * @param marker
   */
  private updateVideoOverLay(marker: Marker): void {
    if (marker) {
      this.markerState.emit(marker);
    }
  }

  /**
   * Add getter for active marker state
   */
  isMarkerActive(marker: Marker): boolean {
    return this.activeMarker() === marker;
  }

  /**
   * Custom toggle play/pause button
   */
  togglePlay(): void {
    if (this.isToggling || !this.player) return;

    this.isToggling = true;
    let prePauseTime = this.player.currentTime();

    if (this.player) {
      if (this.player.paused()) {
        // console.log('Pausing video time start', prePauseTime);
        // TODO: Need to fix this or remove this promise timing issue
        this.player
          .play()
          .then(() => {
            console.log('Toggle Play - Playing video');
            this.isPlaying.set(true);
            this.playStarted.emit(true); // Emit event when play starts
            // Reset active marker when playing
            this.activeMarker.set(null);
            this.isToggling = false;
          })
          .catch((error) => {
            console.error('Error playing video:', error);
          })
          .finally(() => {
            this.isToggling = false;
          });
      } else {
        console.log('Toggle Play - Pausing video');
        this.player.pause();
        this.player.currentTime(prePauseTime);
        this.isPlaying.set(false);
        this.isToggling = false;
        this.playStarted.emit(false); // Emit event when play starts
      }
    }
  }

  /**
   * Return boolean if video is paused
   * @returns boolean
   */
  isPaused(): boolean {
    return this.player && this.player.paused();
  }

  /**
   * Calculate the viewport size of the video player
   */
  private calculateViewPortSize(): void {
    if (!this.player) return;
    this.aspectRatio = this.player.currentHeight() / this.player.currentWidth();
    this.windowRatio = window.innerHeight / window.innerWidth;

    // Emit viewport size
    const viewportSize: ViewportSize = {
      playerWidth: this.player.currentWidth(), // This is the real player's actual width
      playerHeight: this.player.currentHeight(), // This is the real player's actual height
      browserWidth: window.innerWidth,
      browserHeight: window.innerHeight,
      aspectRatio: this.aspectRatio,
      windowRatio: this.windowRatio,
    };

    this.videoResizeEvent.emit(viewportSize);
  }

  /**
   * Handle video resize event
   * Set the aspect ratio of the video player
   */
  // private handleResizeVideo(): void {
  //   if (!this.player) return;

  //   // Your existing resize logic
  //   if (this.windowRatio > this.aspectRatio) {
  //     this.player.addClass('vjs-16-9');
  //     this.player.removeClass('large-screen');
  //     this.player.addClass('small-screen');
  //     this.calculateViewPortSize();
  //   } else {
  //     this.player.removeClass('vjs-16-9');
  //     this.player.addClass('large-screen');
  //     this.player.removeClass('small-screen');
  //     this.calculateViewPortSize();
  //   }
  // }

  /**
   * Setup Resize Observer to detect when the video player is resized
   */
  private setupResizeObserver(): void {
    if (!this.player) return;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.resizeObserver = new ResizeObserver((entries) => {
      // ResizeObserver entry contains:
      // entry.contentRect: { width, height, top, left, right, bottom }
      // entry.contentBoxSize: array containing the element's content box size
      // entry.borderBoxSize: array containing the element's border box size
      // entry.devicePixelContentBoxSize: array containing the element's content box size in device pixels

      // However, for video.js, we'll use the player's methods instead
      this.calculateViewPortSize();
    });

    // Observe the player's wrapper element
    const playerElement = this.player.el();
    if (playerElement) {
      // console.log('resized playerElement', playerElement);
      this.resizeObserver.observe(playerElement);
    }
  }

  /**
   * Dispose video player onDestroy
   */
  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.player) {
      this.player.dispose();
    }
  }
}
